import React from 'react'
import { ApolloProvider, useApolloClient } from '@apollo/react-hooks'
import ApolloClient from 'apollo-boost'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { ShopConfig } from '../SlerpShop'
import env from '../env'

const scheme = (proto: string) => {
  if (process.env.NODE_ENV === 'production') {
    return 'https'
  } else {
    if (env.GRAPH_HOST === 'graph.api.slerpdemo.com') return 'https'
    return window.location.protocol === 'https:' ? `${proto}s` : proto
  }
}

const GRAPH_HOST =
  process.env.NODE_ENV === 'production'
    ? env.GRAPH_HOST
    : process.env.REACT_APP_GRAPH_HOST || env.GRAPH_HOST

export const GRAPHQL_ENDPOINT = `${scheme('http')}://${GRAPH_HOST}/v1/graphql`

/**
 * This creates a new Apollo Client.
 * This is being used by ShopClient or can be called as a standalone function
 *
 * @param domain
 * @param apiKey
 */
const createClient = (domain: string, apiKey: string) => {
  const client = new ApolloClient({
    uri: GRAPHQL_ENDPOINT,
    cache: new InMemoryCache({
      addTypename: false
    }),
    headers: {
      authorization: apiKey ? `Bearer ${apiKey}` : ''
    }
  })

  return client
}

/**
 *  Shop props
 */
interface ShopProps {
  config: ShopConfig
  children: React.ReactNode
}

/**
 *  A hook to return a wrapped graphql client
 *
 *  @returns ApolloClient
 */
const useShopClient = () => {
  return useApolloClient()
}

/**
 * The Slerp context provider that allows the app to connect
 * to the GraphQL endpoint
 *
 * @param props An object that accepts a config (ShopConfig) and its children.
 *
 * @example
 *
 *  const config = {
 *   apiKey: 'someapikey',
 *   domain: 'crosstown'
 *  }
 *
 *  <ShopClient config={config}>
 *    <SomeComponent />
 *  </ShopClient>
 *
 */
const ShopClient = (props: ShopProps) => {
  const {
    config: { domain, apiKey }
  } = props

  return (
    <ApolloProvider client={createClient(domain, apiKey)}>
      {props.children}
    </ApolloProvider>
  )
}

export * from './queries'
// export * from './mutations/CheckoutMutations'
export { useShopClient, createClient }
export default ShopClient
